import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronDown,
  FaClipboardList,
  FaCopy,
  FaExternalLinkAlt,
  FaPhone,
  FaPlay,
  FaPlus,
  FaReply,
  FaList,
  FaMapMarkerAlt,
  FaTimes,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import { createTemplate } from "../../api/templates.api";
import { apihubGet } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import {
  TEMPLATE_LANGUAGES,
  resolveLanguageCode,
} from "../../data/templateLanguages";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const EMPTY_FORM = {
  category: "",
  language: "en",
  name: "",
  type: "",
  headertext: "",
  format: "",
  footer: "",
  carouselMediaType: "",
  interactiveActions: "None",
  actions: [],
  disclaimer: "",
  placeholderHeaderValues: {},
  placeholderValues: {},
  syncedCarouselCardsData: [],
  currentwabaId: "",
  currentPhoneNumberId: "",
  expirationTime: "",
  otpType: "COPY_CODE",
  otpButtonText: "Copy Code",
  otpAutofillText: "Autofill",
  otpPackageName: "",
  otpSignatureHash: "",
  otpZeroTapTerms: false,
};

const AUTH_PREVIEW_BY_OTP = {
  COPY_CODE: {
    params:
      "OTP Code: {{code}}. This is your OTP code for {{text}}. For your security, do not share this code.",
    button: "Copy code",
  },
  ONE_TAP: {
    params:
      "{{code}} is your verification code. Tap Autofill to continue. For your security, do not share this code.",
    button: "Autofill",
  },
  ZERO_TAP: {
    params:
      "{{code}} is your verification code. It will be filled in automatically. For your security, do not share this code.",
    button: "Copy code",
  },
};

function fillAuthPreviewExample(text = "", samples = {}) {
  return String(text)
    .replace(/\{\{\s*code\s*\}\}/gi, samples.code || samples[1] || "123456")
    .replace(/\{\{\s*text\s*\}\}/gi, samples.text || samples[2] || "login")
    .replace(/\{\{(\d+)\}\}/g, (_, n) => {
      const sample = samples[n] ?? samples[String(n)];
      if (sample) return String(sample);
      if (n === "1") return "123456";
      if (n === "2") return "login";
      return `sample${n}`;
    });
}

function highlightTemplateVars(text = "") {
  return String(text)
    .split(/(\{\{[^}]+\}\})/g)
    .map((part, index) =>
      /^\{\{[^}]+\}\}$/.test(part) ? (
        <span key={index} className="create-template-wa-var">
          {part}
        </span>
      ) : (
        part
      ),
    );
}

const OTP_DELIVERY_OPTIONS = [
  {
    value: "ZERO_TAP",
    label: "Zero-tap autofill",
    hint: "This is recommended as the easiest option for your customers. Zero-tap will automatically send code without requiring your customer to tap a button. An autofill or copy code message will be sent if zero-tap and autofill aren't possible.",
  },
  {
    value: "ONE_TAP",
    label: "One-tap autofill",
    hint: "The code sends to your app when customers tap the button. A copy code message will be sent if autofill isn't possible.",
  },
  {
    value: "COPY_CODE",
    label: "Copy code",
    hint: "Basic authentication with quick setup. Customers copy and paste the code into your app.",
  },
];

const MARKETING_TYPES = [
  "Text",
  "Image",
  "Document",
  "Video",
  "Location",
  "Order Details",
  "Carousel",
];

const MEDIA_SAMPLE_TYPES = {
  Image: {
    accept: "image/jpeg,image/png,image/jpg",
    maxMb: 5,
    label: "JPG or PNG, up to 5 MB",
  },
  Video: {
    accept: "video/mp4",
    maxMb: 16,
    label: "MP4, up to 16 MB",
  },
  Document: {
    accept: "application/pdf",
    maxMb: 100,
    label: "PDF, up to 100 MB",
  },
};

const BUTTON_OPTIONS = [
  {
    type: "QUICK_REPLY",
    label: "Custom",
    icon: FaReply,
  },
  {
    type: "URL",
    label: "Visit website",
    icon: FaExternalLinkAlt,
  },
  {
    type: "VOICE_CALL",
    label: "Call on WhatsApp",
    icon: FaWhatsapp,
  },
  {
    type: "PHONE_NUMBER",
    label: "Call phone number",
    icon: FaPhone,
  },
  {
    type: "FLOW",
    label: "Complete Flow",
    hint: "1 button maximum",
    icon: FaClipboardList,
    max: 1,
  },
  {
    type: "SHARE_CONTACT",
    label: "Share Contact Info",
    icon: FaUser,
    disabled: true,
  },
];

const BUTTON_LIMITS = {
  QUICK_REPLY: 10,
  URL: 2,
  VOICE_CALL: 1,
  PHONE_NUMBER: 1,
  FLOW: 1,
};

const PREVIEW_BUTTON_ICONS = {
  QUICK_REPLY: FaReply,
  URL: FaExternalLinkAlt,
  VOICE_CALL: FaWhatsapp,
  PHONE_NUMBER: FaPhone,
  FLOW: FaClipboardList,
  SHARE_CONTACT: FaUser,
};

const PHONE_COUNTRIES = [
  { code: "+91", name: "India", short: "IN +91" },
  { code: "+1", name: "United States", short: "US +1" },
  { code: "+44", name: "United Kingdom", short: "UK +44" },
  { code: "+971", name: "United Arab Emirates", short: "AE +971" },
  { code: "+65", name: "Singapore", short: "SG +65" },
  { code: "+61", name: "Australia", short: "AU +61" },
  { code: "+92", name: "Pakistan", short: "PK +92" },
  { code: "+880", name: "Bangladesh", short: "BD +880" },
  { code: "+94", name: "Sri Lanka", short: "LK +94" },
  { code: "+977", name: "Nepal", short: "NP +977" },
  { code: "+966", name: "Saudi Arabia", short: "SA +966" },
  { code: "+974", name: "Qatar", short: "QA +974" },
  { code: "+965", name: "Kuwait", short: "KW +965" },
  { code: "+973", name: "Bahrain", short: "BH +973" },
  { code: "+968", name: "Oman", short: "OM +968" },
  { code: "+60", name: "Malaysia", short: "MY +60" },
  { code: "+62", name: "Indonesia", short: "ID +62" },
  { code: "+63", name: "Philippines", short: "PH +63" },
  { code: "+66", name: "Thailand", short: "TH +66" },
  { code: "+49", name: "Germany", short: "DE +49" },
  { code: "+33", name: "France", short: "FR +33" },
  { code: "+27", name: "South Africa", short: "ZA +27" },
  { code: "+234", name: "Nigeria", short: "NG +234" },
  { code: "+55", name: "Brazil", short: "BR +55" },
];

const emptyButton = (type) => ({
  type,
  title: "",
  value: "",
  countrycode: "+91",
  urlType: "static",
  exampleUrl: "",
  flowAction: "NAVIGATE",
  navigateScreen: "",
});

const deriveInteractiveActions = (actions = []) => {
  if (!actions.length) return "None";
  const types = new Set(actions.map((action) => action.type));
  const hasQr = types.has("QUICK_REPLY");
  const hasCta =
    types.has("URL") || types.has("PHONE_NUMBER") || types.has("VOICE_CALL");
  const hasFlow = types.has("FLOW");
  if (hasFlow && !hasQr && !hasCta) return "Flow Button";
  if (hasQr && !hasCta && !hasFlow) return "Quick Replies";
  if (hasCta && !hasQr && !hasFlow) return "Call to Actions";
  return "All";
};

const mapLibraryTemplate = (tpl) => {
  if (!tpl) return {};
  const actions = Array.isArray(tpl.interactiveActions)
    ? tpl.interactiveActions.map((a) => ({
        type: a.type,
        title: a.title || "",
        value: a.value || "",
        countrycode: a.countrycode || "+91",
        urlType: a.urlType || "static",
        exampleUrl: a.exampleUrl || "",
        flowAction: a.flowAction || a.flow_action || "NAVIGATE",
        navigateScreen: a.navigateScreen || a.navigate_screen || "",
      }))
    : [];
  let interactiveActions = "None";
  if (actions.length) {
    if (actions.some((a) => a.type === "URL" || a.type === "PHONE_NUMBER")) {
      interactiveActions = "Call to Actions";
    } else if (actions.some((a) => a.type === "QUICK_REPLY")) {
      interactiveActions = "Quick Replies";
    } else {
      interactiveActions = "All";
    }
  }
  return {
    category: (tpl.category || "").toLowerCase(),
    language: resolveLanguageCode(tpl.language),
    name: (tpl.templateName || "").toLowerCase().replace(/[^a-z0-9_]/g, "_"),
    type: tpl.templateType || "",
    format: tpl.templateFormat || "",
    footer: tpl.footerText || "",
    interactiveActions,
    actions,
    placeholderValues: tpl.sampleValues || {},
    otpType: tpl.authFeature || "COPY_CODE",
  };
};

const extractPlaceholders = (text = "") => {
  const nums = [...text.matchAll(/{{(\d+)}}/g)].map((m) => Number(m[1]));
  return [...new Set(nums)].sort((a, b) => a - b);
};

const renumberPlaceholders = (text = "") => {
  let i = 1;
  return text.replace(/{{(\d+)}}/g, () => `{{${i++}}}`);
};

const CreateTemplate = () => {
  const { projectId, wabaId, phoneNoId, phoneReady } = useProjectPhone();
  const navigate = useNavigate();
  const location = useLocation();
  const libraryTemplate = location.state?.template || location.state?.libraryTemplate;

  const [form, setForm] = useState(EMPTY_FORM);
  const [langQuery, setLangQuery] = useState("English");
  const [langOpen, setLangOpen] = useState(false);
  const [langMenuPos, setLangMenuPos] = useState(null);
  const [headerPlaceholders, setHeaderPlaceholders] = useState([]);
  const [bodyPlaceholders, setBodyPlaceholders] = useState([]);
  const [expirationOn, setExpirationOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [previewTab, setPreviewTab] = useState("parameters");
  const [sampleFile, setSampleFile] = useState(null);
  const [samplePreview, setSamplePreview] = useState("");
  const [sampleDragOver, setSampleDragOver] = useState(false);
  const [buttonMenuOpen, setButtonMenuOpen] = useState(false);
  const [buttonMenuPos, setButtonMenuPos] = useState(null);
  const [previewSeeAll, setPreviewSeeAll] = useState(false);
  const [metaFlows, setMetaFlows] = useState([]);
  const sampleInputRef = useRef(null);
  const buttonMenuRef = useRef(null);
  const addButtonRef = useRef(null);
  const langFieldRef = useRef(null);
  const langListRef = useRef(null);

  useEffect(() => {
    if (libraryTemplate) {
      const mapped = mapLibraryTemplate(libraryTemplate);
      setForm((prev) => ({ ...prev, ...mapped }));
      setLangQuery(
        TEMPLATE_LANGUAGES.find((l) => l.value === mapped.language)?.label ||
          mapped.language ||
          "",
      );
      setHeaderPlaceholders(extractPlaceholders(mapped.headertext || ""));
      setBodyPlaceholders(extractPlaceholders(mapped.format || ""));
    }
  }, [libraryTemplate]);

  useEffect(() => {
    if (!phoneReady) return;
    setForm((prev) => ({
      ...prev,
      currentwabaId: wabaId || "",
      currentPhoneNumberId: phoneNoId || "",
    }));
  }, [wabaId, phoneNoId, phoneReady]);

  useEffect(() => {
    return () => {
      if (samplePreview) URL.revokeObjectURL(samplePreview);
    };
  }, [samplePreview]);

  useEffect(() => {
    if (!buttonMenuOpen) return undefined;

    const placeMenu = () => {
      const el = addButtonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuHeight = 320;
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight && rect.top > menuHeight;
      setButtonMenuPos({
        left: Math.min(rect.left, window.innerWidth - 280),
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        minWidth: Math.max(260, rect.width),
      });
    };

    placeMenu();
    const onPointerDown = (event) => {
      if (
        buttonMenuRef.current?.contains(event.target) ||
        addButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setButtonMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [buttonMenuOpen]);

  useEffect(() => {
    if (!langOpen) {
      setLangMenuPos(null);
      return undefined;
    }

    const placeMenu = () => {
      const el = langFieldRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuHeight = 280;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight && rect.top > menuHeight;
      setLangMenuPos({
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      });
    };

    placeMenu();
    const onPointerDown = (event) => {
      if (
        langListRef.current?.contains(event.target) ||
        langFieldRef.current?.contains(event.target)
      ) {
        return;
      }
      setLangOpen(false);
      setLangQuery(
        TEMPLATE_LANGUAGES.find((l) => l.value === form.language)?.label ||
          "English",
      );
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [langOpen, form.language]);

  const hasFlowButton = form.actions.some((action) => action.type === "FLOW");

  useEffect(() => {
    if (!projectId || !hasFlowButton) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apihubGet(
          `/getInteractedCustomerDetail/getallflows?projectId=${encodeURIComponent(
            projectId,
          )}&type=initial`,
        );
        if (!cancelled) {
          setMetaFlows(Array.isArray(res?.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setMetaFlows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, hasFlowButton]);

  const filteredLanguages = useMemo(() => {
    const q = langQuery.trim().toLowerCase();
    if (!q) return TEMPLATE_LANGUAGES;
    return TEMPLATE_LANGUAGES.filter(
      (l) =>
        l.label.toLowerCase().includes(q) ||
        l.value.toLowerCase().includes(q),
    );
  }, [langQuery]);

  const selectedLangLabel = useMemo(
    () => TEMPLATE_LANGUAGES.find((l) => l.value === form.language)?.label || "",
    [form.language],
  );

  const formatCharCount = form.format.length;

  const authPreviewPreset =
    AUTH_PREVIEW_BY_OTP[form.otpType] || AUTH_PREVIEW_BY_OTP.COPY_CODE;
  const authPreviewSource = form.format.trim() || authPreviewPreset.params;
  const authPreviewBody =
    previewTab === "example"
      ? fillAuthPreviewExample(authPreviewSource, form.placeholderValues)
      : authPreviewSource;
  const authPreviewButton =
    form.otpType === "ONE_TAP"
      ? form.otpAutofillText || authPreviewPreset.button
      : authPreviewPreset.button;

  const actionCounts = useMemo(
    () =>
      form.actions.reduce((acc, action) => {
        acc[action.type] = (acc[action.type] || 0) + 1;
        return acc;
      }, {}),
    [form.actions],
  );

  const quickReplyActions = useMemo(
    () =>
      form.actions
        .map((action, index) => ({ action, index }))
        .filter(({ action }) => action.type === "QUICK_REPLY"),
    [form.actions],
  );

  const callToActionButtons = useMemo(
    () =>
      form.actions
        .map((action, index) => ({ action, index }))
        .filter(({ action }) => action.type !== "QUICK_REPLY"),
    [form.actions],
  );

  const previewHeader = useMemo(() => {
    let text = form.headertext || "";
    if (!text) return "";
    text = text.replace(/{{(\d+)}}/g, (_, n) => {
      const sample = form.placeholderHeaderValues[n];
      return sample ? `[${sample}]` : `{{${n}}}`;
    });
    return text;
  }, [form.headertext, form.placeholderHeaderValues]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  };

  const onTypeChange = (e) => {
    const type = e.target.value;
    setField("type", type);
    setSampleFile(null);
    setSampleDragOver(false);
    setSamplePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const applySampleFile = (file, inputEl) => {
    const spec = MEDIA_SAMPLE_TYPES[form.type];
    setSamplePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    if (!file) {
      setSampleFile(null);
      return;
    }
    if (spec && file.size > spec.maxMb * 1024 * 1024) {
      setError(`Sample ${form.type.toLowerCase()} must be ${spec.label}.`);
      if (inputEl) inputEl.value = "";
      setSampleFile(null);
      return;
    }
    setSampleFile(file);
    if (form.type === "Image" || form.type === "Video") {
      setSamplePreview(URL.createObjectURL(file));
    }
    setError("");
  };

  const onSampleFileChange = (e) => {
    applySampleFile(e.target.files?.[0] || null, e.target);
  };

  const onSampleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSampleDragOver(false);
    applySampleFile(e.dataTransfer?.files?.[0] || null, sampleInputRef.current);
  };

  const onCategoryChange = (e) => {
    const category = e.target.value;
    setForm((prev) => ({
      ...prev,
      category,
      language: prev.language || "en",
      type: category === "authentication" ? "Text" : "",
      format: category === "authentication" ? "" : prev.format,
      interactiveActions: "None",
      actions: [],
      otpType: category === "authentication" ? "COPY_CODE" : prev.otpType,
      otpZeroTapTerms: false,
    }));
    setLangQuery((query) => query || "English");
    setLangOpen(false);
  };

  const onNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setField("name", value);
  };

  const onHeaderChange = (e) => {
    let value = e.target.value;
    if (value.length > 60) return;
    const matches = [...value.matchAll(/{{(\d+)}}/g)].map((m) => Number(m[1]));
    if (matches.length > 1) {
      setError("Only one variable {{1}} is allowed in the header.");
      return;
    }
    if (matches.length === 1 && matches[0] !== 1) {
      value = value.replace(/{{\d+}}/g, "{{1}}");
    }
    const nums = extractPlaceholders(value);
    setHeaderPlaceholders(nums);
    setForm((prev) => ({
      ...prev,
      headertext: value,
      placeholderHeaderValues: nums.reduce((acc, n) => {
        acc[n] = prev.placeholderHeaderValues[n] || "";
        return acc;
      }, {}),
    }));
    setError("");
  };

  const onFormatChange = (e) => {
    let value = e.target.value;
    if (value.length > 1024) return;
    const renumbered = renumberPlaceholders(value);
    const nums = extractPlaceholders(renumbered);
    setBodyPlaceholders(nums);
    setForm((prev) => ({
      ...prev,
      format: renumbered,
      placeholderValues: nums.reduce((acc, n) => {
        acc[n] = prev.placeholderValues[n] || "";
        return acc;
      }, {}),
    }));
    setError("");
  };

  const addAction = (type) => {
    setForm((prev) => {
      const counts = prev.actions.reduce((acc, action) => {
        acc[action.type] = (acc[action.type] || 0) + 1;
        return acc;
      }, {});
      const limit = BUTTON_LIMITS[type];
      if (limit && (counts[type] || 0) >= limit) return prev;
      if (prev.actions.length >= 10) return prev;
      const actions = [...prev.actions, emptyButton(type)];
      return {
        ...prev,
        actions,
        interactiveActions: deriveInteractiveActions(actions),
      };
    });
    setButtonMenuOpen(false);
  };

  const updateAction = (index, field, value) => {
    setForm((prev) => {
      const actions = [...prev.actions];
      const current = { ...actions[index], [field]: value };
      if (field === "urlType" && value === "dynamic") {
        const url = String(current.value || "").trim();
        if (url && !/\{\{\d+\}\}/.test(url)) {
          current.value = `${url.replace(/\/$/, "")}/{{1}}`;
        }
      }
      actions[index] = current;
      return { ...prev, actions };
    });
  };

  const changeActionType = (index, nextType) => {
    setForm((prev) => {
      const current = prev.actions[index];
      if (!current || current.type === nextType) return prev;
      const counts = prev.actions.reduce((acc, action) => {
        acc[action.type] = (acc[action.type] || 0) + 1;
        return acc;
      }, {});
      const limit = BUTTON_LIMITS[nextType];
      if (limit && (counts[nextType] || 0) >= limit) return prev;
      const actions = [...prev.actions];
      actions[index] = { ...emptyButton(nextType), title: current.title || "" };
      return {
        ...prev,
        actions,
        interactiveActions: deriveInteractiveActions(actions),
      };
    });
  };

  const removeAction = (index) => {
    setForm((prev) => {
      const actions = prev.actions.filter((_, i) => i !== index);
      return {
        ...prev,
        actions,
        interactiveActions: deriveInteractiveActions(actions),
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phoneReady || !wabaId || !phoneNoId) {
      setError("Connect WhatsApp on My Profile before creating templates.");
      return;
    }
    if (!form.category || !form.language || !form.name || !form.type) {
      setError("Please fill all required fields.");
      return;
    }
    if (form.category !== "authentication" && !form.format.trim()) {
      setError("Template body is required.");
      return;
    }
    if (form.category === "authentication") {
      if (!form.otpType) {
        setError("Choose how customers send the code from WhatsApp.");
        return;
      }
      if (
        (form.otpType === "ONE_TAP" || form.otpType === "ZERO_TAP") &&
        (!form.otpPackageName.trim() || !form.otpSignatureHash.trim())
      ) {
        setError(
          "Android package name and app signing key hash are required for autofill.",
        );
        return;
      }
      if (form.otpType === "ZERO_TAP" && !form.otpZeroTapTerms) {
        setError("Accept the zero-tap terms to continue.");
        return;
      }
    }
    if (MEDIA_SAMPLE_TYPES[form.type] && !sampleFile) {
      setError(`Upload a sample ${form.type.toLowerCase()} for the template header.`);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        currentwabaId: wabaId,
        currentPhoneNumberId: phoneNoId,
        projectId,
        expirationTime: expirationOn ? form.expirationTime : "",
      };
      const res = await createTemplate(payload, sampleFile);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to submit template");
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Failed to submit template");
    } finally {
      setSubmitting(false);
    }
  };

  const showHeaderField =
    (form.category === "marketing" || form.category === "utility") &&
    (form.type === "Text" || form.type === "Order Details");
  const mediaSample = MEDIA_SAMPLE_TYPES[form.type];

  const showBodyField = form.category !== "authentication";

  const typeOptions =
    form.category === "authentication"
      ? ["Text"]
      : form.category === "marketing" || form.category === "utility"
        ? MARKETING_TYPES
        : [];

  const renderActionRow = (action, index) => {
    const isQr = action.type === "QUICK_REPLY";
    const titleLen = (action.title || "").length;
    const phoneDigits = String(action.value || "").replace(/\D/g, "");
    return (
      <div
        key={`${action.type}-${index}`}
        className="create-template-action-row-wrap"
      >
        <div className="create-template-action-row-meta">
          <label className="create-template-action-col-type">
            <span>{isQr ? "Type" : "Type of Action"}</span>
            <select
              value={action.type}
              onChange={(e) => changeActionType(index, e.target.value)}
            >
              {BUTTON_OPTIONS.filter((option) => !option.disabled).map(
                (option) => {
                  const atLimit =
                    Boolean(BUTTON_LIMITS[option.type]) &&
                    (actionCounts[option.type] || 0) >=
                      BUTTON_LIMITS[option.type] &&
                    option.type !== action.type;
                  return (
                    <option
                      key={option.type}
                      value={option.type}
                      disabled={atLimit}
                    >
                      {option.label}
                    </option>
                  );
                },
              )}
            </select>
          </label>
          <label className="create-template-action-col-text">
            <span>Button Text</span>
            <div className="create-template-action-input-wrap">
              <input
                type="text"
                placeholder="Add button text"
                value={action.title}
                maxLength={25}
                onChange={(e) => updateAction(index, "title", e.target.value)}
              />
              <em>{titleLen}/25</em>
            </div>
          </label>
          {action.type === "URL" && (
            <>
              <label className="create-template-action-col-urltype">
                <span>URL type</span>
                <select
                  value={action.urlType || "static"}
                  onChange={(e) =>
                    updateAction(index, "urlType", e.target.value)
                  }
                >
                  <option value="static">Static</option>
                  <option value="dynamic">Dynamic</option>
                </select>
              </label>
              <label className="create-template-action-col-url">
                <span>Website URL</span>
                <input
                  type="url"
                  placeholder={
                    action.urlType === "dynamic"
                      ? "https://www.example.com/{{1}}"
                      : "https://www.example.com"
                  }
                  value={action.value}
                  onChange={(e) =>
                    updateAction(index, "value", e.target.value)
                  }
                />
              </label>
              {action.urlType === "dynamic" && (
                <label className="create-template-action-col-url">
                  <span>Sample URL</span>
                  <input
                    type="url"
                    placeholder="https://www.example.com/order123"
                    value={action.exampleUrl || ""}
                    onChange={(e) =>
                      updateAction(index, "exampleUrl", e.target.value)
                    }
                  />
                </label>
              )}
            </>
          )}
          {action.type === "PHONE_NUMBER" && (
            <>
              <label className="create-template-action-col-country">
                <span>Country</span>
                <select
                  value={action.countrycode || "+91"}
                  onChange={(e) =>
                    updateAction(index, "countrycode", e.target.value)
                  }
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.short}
                    </option>
                  ))}
                </select>
              </label>
              <label className="create-template-action-col-phone">
                <span>Phone number</span>
                <div className="create-template-action-input-wrap">
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={action.value}
                    maxLength={20}
                    onChange={(e) =>
                      updateAction(index, "value", e.target.value)
                    }
                  />
                  <em>{phoneDigits.length}/20</em>
                </div>
              </label>
            </>
          )}
          {action.type === "FLOW" && (
            <>
              <label className="create-template-action-col-flow">
                <span>Flow</span>
                <select
                  value={action.value}
                  onChange={(e) =>
                    updateAction(index, "value", e.target.value)
                  }
                >
                  <option value="">Select a flow</option>
                  {metaFlows.map((flow) => {
                    const id = String(flow.id || flow._id || "");
                    return (
                      <option key={id} value={id}>
                        {flow.name || id}
                        {flow.status ? ` (${flow.status})` : ""}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="create-template-action-col-urltype">
                <span>Flow action</span>
                <select
                  value={action.flowAction || "NAVIGATE"}
                  onChange={(e) =>
                    updateAction(index, "flowAction", e.target.value)
                  }
                >
                  <option value="NAVIGATE">Navigate</option>
                  <option value="DATA_EXCHANGE">Data exchange</option>
                </select>
              </label>
              {(action.flowAction || "NAVIGATE") === "NAVIGATE" && (
                <label className="create-template-action-col-flow">
                  <span>Screen</span>
                  <input
                    type="text"
                    placeholder="WELCOME_SCREEN"
                    value={action.navigateScreen || ""}
                    onChange={(e) =>
                      updateAction(index, "navigateScreen", e.target.value)
                    }
                  />
                </label>
              )}
            </>
          )}
          <button
            type="button"
            className="create-template-action-remove"
            onClick={() => removeAction(index)}
            aria-label="Remove button"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    );
  };

  const previewTime = useMemo(
    () =>
      new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    [],
  );

  return (
    <div className="create-template-page">
      <header className="create-template-header">
        <div className="create-template-header-text">
          <h1>Create Template</h1>
          <p>
            Compose a WhatsApp message template, add optional buttons, and preview
            how it will look before submitting it to Meta for review.
          </p>
        </div>
        <Link
          to={businessPath(projectId, "fetchalltemplates")}
          className="create-template-back"
        >
          <FaArrowLeft />
          Back
        </Link>
      </header>
      <div className="create-template-layout">
        <div className="create-template-form-col">
          <form className="create-template-form" onSubmit={onSubmit}>

            {success && (
              <div className="create-template-success">
                <p>
                  Template submitted successfully! It may take time for Meta to
                  approve.
                </p>
                <button
                  type="button"
                  className="create-template-submit"
                  onClick={() =>
                    navigate(businessPath(projectId, "fetchalltemplates"))
                  }
                >
                  View Templates
                </button>
              </div>
            )}
            {error && <p className="create-template-error">{error}</p>}

            <section className="create-template-section">
              <h2>Template details</h2>
              <div className="create-template-grid">
                <label className="create-template-field">
                  <span>Category</span>
                  <small>Choose the Meta category for this template.</small>
                  <select value={form.category} onChange={onCategoryChange} required>
                    <option value="">Select category</option>
                    <option value="marketing">Marketing</option>
                    <option value="authentication">Authentication</option>
                    <option value="utility">Utility</option>
                  </select>
                </label>

                <label className="create-template-field create-template-field-lang">
                  <span>Language</span>
                  <small>Language used when the template is submitted.</small>
                  <div className="create-template-lang-control" ref={langFieldRef}>
                    <input
                      type="text"
                      value={langOpen ? langQuery : selectedLangLabel || "English"}
                      onChange={(e) => {
                        setLangQuery(e.target.value);
                        setLangOpen(true);
                      }}
                      onClick={() => {
                        if (!langOpen) {
                          setLangOpen(true);
                          setLangQuery("");
                        }
                      }}
                      onFocus={() => {
                        if (!langOpen) {
                          setLangOpen(true);
                          setLangQuery("");
                        }
                      }}
                      placeholder="Search for a language..."
                      autoComplete="off"
                      required={!form.language}
                    />
                    <button
                      type="button"
                      className="create-template-lang-caret"
                      aria-label={langOpen ? "Close languages" : "Open languages"}
                      onClick={() => {
                        setLangOpen((open) => {
                          const next = !open;
                          if (next) setLangQuery("");
                          else {
                            setLangQuery(selectedLangLabel || "English");
                          }
                          return next;
                        });
                      }}
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                  {langOpen &&
                    langMenuPos &&
                    createPortal(
                      <div
                        ref={langListRef}
                        className="create-template-lang-list"
                        role="listbox"
                        style={{
                          left: langMenuPos.left,
                          width: langMenuPos.width,
                          top: langMenuPos.top,
                          bottom: langMenuPos.bottom,
                        }}
                      >
                        {filteredLanguages.length === 0 ? (
                          <p className="create-template-lang-empty">
                            No matching language
                          </p>
                        ) : (
                          filteredLanguages.map((lang) => (
                            <button
                              key={lang.value}
                              type="button"
                              role="option"
                              aria-selected={form.language === lang.value}
                              className={
                                form.language === lang.value ? "is-selected" : ""
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setField("language", lang.value);
                                setLangQuery(lang.label);
                                setLangOpen(false);
                              }}
                            >
                              {lang.label}
                            </button>
                          ))
                        )}
                      </div>,
                      document.body,
                    )}
                </label>

                <label className="create-template-field">
                  <span>Template name</span>
                  <small>Lowercase letters, numbers, and underscores only.</small>
                  <input
                    type="text"
                    value={form.name}
                    onChange={onNameChange}
                    placeholder="welcome_message"
                    maxLength={512}
                    required
                  />
                </label>

                <label className="create-template-field">
                  <span>Template type</span>
                  <small>Header format for this message.</small>
                  <select
                    value={form.type}
                    onChange={onTypeChange}
                    required
                    disabled={!form.category}
                  >
                    <option value="">Select type</option>
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            {form.category && (
            <section className="create-template-section">
              <h2>Message content</h2>
            {form.category === "authentication" && (
              <fieldset className="create-template-field create-template-otp-setup">
                <legend>Code delivery setup</legend>
                <small>
                  Choose how customers send the code from WhatsApp to your app.
                </small>
                <div className="create-template-otp-options">
                  {OTP_DELIVERY_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={
                        form.otpType === opt.value
                          ? "create-template-otp-option is-selected"
                          : "create-template-otp-option"
                      }
                    >
                      <input
                        type="radio"
                        name="otpType"
                        checked={form.otpType === opt.value}
                        onChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            otpType: opt.value,
                            format: "",
                          }))
                        }
                      />
                      <span>
                        <strong>{opt.label}</strong>
                        <em>{opt.hint}</em>
                      </span>
                    </label>
                  ))}
                </div>

                {(form.otpType === "ONE_TAP" || form.otpType === "ZERO_TAP") && (
                  <div className="create-template-otp-app">
                    <label className="create-template-field">
                      <span>Android package name</span>
                      <input
                        type="text"
                        value={form.otpPackageName}
                        onChange={(e) =>
                          setField("otpPackageName", e.target.value)
                        }
                        placeholder="com.example.app"
                        required
                      />
                    </label>
                    <label className="create-template-field">
                      <span>App signing key hash</span>
                      <input
                        type="text"
                        value={form.otpSignatureHash}
                        onChange={(e) =>
                          setField("otpSignatureHash", e.target.value)
                        }
                        placeholder="Signature hash"
                        required
                      />
                    </label>
                    {form.otpType === "ONE_TAP" && (
                      <label className="create-template-field">
                        <span>Autofill button text (optional)</span>
                        <input
                          type="text"
                          value={form.otpAutofillText}
                          onChange={(e) =>
                            setField("otpAutofillText", e.target.value)
                          }
                          placeholder="Autofill"
                          maxLength={25}
                        />
                      </label>
                    )}
                    {form.otpType === "ZERO_TAP" && (
                      <label className="create-template-otp-terms">
                        <input
                          type="checkbox"
                          checked={Boolean(form.otpZeroTapTerms)}
                          onChange={(e) =>
                            setField("otpZeroTapTerms", e.target.checked)
                          }
                        />
                        I accept the WhatsApp zero-tap autofill terms.
                      </label>
                    )}
                  </div>
                )}
              </fieldset>
            )}

            {form.type === "Carousel" && (
              <label className="create-template-field">
                <span>Carousel Media Type</span>
                <select
                  value={form.carouselMediaType}
                  onChange={(e) => setField("carouselMediaType", e.target.value)}
                  required
                >
                  <option value="">Select carousel media type</option>
                  <option value="IMAGE">Image</option>
                  <option value="VIDEO">Video</option>
                </select>
              </label>
            )}

            {mediaSample && (
              <div className="create-template-media-sample">
                <p className="create-template-media-sample-label">
                  Media sample
                  <em>Optional</em>
                </p>
                <div
                  className={`create-template-dropzone${
                    sampleDragOver ? " is-active" : ""
                  }${sampleFile ? " has-file" : ""}`}
                  onClick={() => sampleInputRef.current?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setSampleDragOver(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setSampleDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setSampleDragOver(false);
                    }
                  }}
                  onDrop={onSampleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      sampleInputRef.current?.click();
                    }
                  }}
                >
                  <input
                    ref={sampleInputRef}
                    type="file"
                    accept={mediaSample.accept}
                    onChange={onSampleFileChange}
                    hidden
                  />
                  {sampleFile ? (
                    <>
                      {form.type === "Image" && samplePreview && (
                        <img src={samplePreview} alt="" />
                      )}
                      {form.type === "Video" && (
                        <span className="create-template-dropzone-play" aria-hidden>
                          <FaPlay />
                        </span>
                      )}
                      <strong>{sampleFile.name}</strong>
                      <span>
                        Or{" "}
                        <button
                          type="button"
                          className="create-template-dropzone-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            sampleInputRef.current?.click();
                          }}
                        >
                          choose files on your device
                        </button>
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>Drag and drop to upload</strong>
                      <span>
                        Or{" "}
                        <button
                          type="button"
                          className="create-template-dropzone-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            sampleInputRef.current?.click();
                          }}
                        >
                          choose files on your device
                        </button>
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {showHeaderField && (
              <label className="create-template-field">
                <span>Template Header Text (Optional)</span>
                <small>Up to 60 characters. Only {"{{1}}"} variable allowed.</small>
                <input
                  type="text"
                  value={form.headertext}
                  onChange={onHeaderChange}
                  placeholder="Enter header text"
                  maxLength={60}
                />
              </label>
            )}

            {headerPlaceholders.length > 0 && (
              <div className="create-template-samples">
                <p>Sample values for header</p>
                {headerPlaceholders.map((n) => (
                  <label key={n}>
                    {`{{${n}}}`}
                    <input
                      type="text"
                      value={form.placeholderHeaderValues[n] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          placeholderHeaderValues: {
                            ...prev.placeholderHeaderValues,
                            [n]: e.target.value,
                          },
                        }))
                      }
                      placeholder="Sample value"
                    />
                  </label>
                ))}
              </div>
            )}

            {showBodyField && (
              <label className="create-template-field">
                <span>Template Format</span>
                <small>
                  Use text formatting - *bold*, _italic_ & ~strikethrough~. Up to
                  1024 characters.
                </small>
                <textarea
                  value={form.format}
                  onChange={onFormatChange}
                  placeholder="Enter your message here..."
                  rows={6}
                  maxLength={1024}
                  required
                />
                <span className="create-template-char-count">
                  Max 1024 characters
                  {formatCharCount > 0 && ` (${formatCharCount}/1024)`}
                </span>
              </label>
            )}

            {bodyPlaceholders.length > 0 && (
              <div className="create-template-samples">
                <p>Sample values for body</p>
                {bodyPlaceholders.map((n) => (
                  <label key={n}>
                    {`{{${n}}}`}
                    <input
                      type="text"
                      value={form.placeholderValues[n] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          placeholderValues: {
                            ...prev.placeholderValues,
                            [n]: e.target.value,
                          },
                        }))
                      }
                      placeholder="Sample value"
                    />
                  </label>
                ))}
              </div>
            )}

            {form.category !== "authentication" && form.type !== "Carousel" && (
              <label className="create-template-field">
                <span>Template Footer (Optional)</span>
                <input
                  type="text"
                  value={form.footer}
                  onChange={(e) => setField("footer", e.target.value)}
                  placeholder="Enter footer text"
                />
              </label>
            )}

            {form.category === "authentication" && (
              <label className="create-template-field">
                <span>Expiration Warning (Optional)</span>
                <small>Time between 1 and 90 minutes.</small>
                <div className="create-template-expiration">
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={form.expirationTime}
                    onChange={(e) => setField("expirationTime", e.target.value)}
                    disabled={!expirationOn}
                    placeholder="Minutes"
                  />
                  <button
                    type="button"
                    className={expirationOn ? "on" : "off"}
                    onClick={() => setExpirationOn((v) => !v)}
                  >
                    {expirationOn ? "On" : "Off"}
                  </button>
                </div>
              </label>
            )}
            </section>
            )}

            {form.type !== "Order Details" && form.category !== "authentication" && (
              <section className="create-template-section">
              <div className="create-template-buttons">
                <h2>
                  Buttons
                  <em>Optional</em>
                </h2>
                <div className="create-template-add-wrap">
                  <button
                    ref={addButtonRef}
                    type="button"
                    className="create-template-add-btn"
                    onClick={() => setButtonMenuOpen((open) => !open)}
                    disabled={form.type === "Carousel" || form.actions.length >= 10}
                    aria-haspopup="menu"
                    aria-expanded={buttonMenuOpen}
                  >
                    <FaPlus />
                    Add button
                    <FaChevronDown />
                  </button>
                  {buttonMenuOpen &&
                    buttonMenuPos &&
                    createPortal(
                    <div
                      ref={buttonMenuRef}
                      className="create-template-add-menu"
                      role="menu"
                      style={{
                        left: buttonMenuPos.left,
                        top: buttonMenuPos.top,
                        bottom: buttonMenuPos.bottom,
                        minWidth: buttonMenuPos.minWidth,
                      }}
                    >
                      {BUTTON_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const atLimit =
                          Boolean(BUTTON_LIMITS[option.type]) &&
                          (actionCounts[option.type] || 0) >=
                            BUTTON_LIMITS[option.type];
                        const disabled =
                          option.disabled ||
                          atLimit ||
                          form.actions.length >= 10;
                        return (
                          <button
                            key={option.type}
                            type="button"
                            role="menuitem"
                            className={`create-template-add-item${
                              disabled ? " is-disabled" : ""
                            }`}
                            disabled={disabled}
                            onClick={() => addAction(option.type)}
                          >
                            <Icon />
                            <span>
                              {option.label}
                              {option.hint && <em>{option.hint}</em>}
                            </span>
                          </button>
                        );
                      })}
                    </div>,
                    document.body,
                  )}
                </div>

                {quickReplyActions.length > 0 && (
                  <div className="create-template-action-group">
                    <p className="create-template-media-sample-label">
                      Quick Reply
                      <em>Optional</em>
                    </p>
                    <div className="create-template-action-table">
                      {quickReplyActions.map(({ action, index }) =>
                        renderActionRow(action, index),
                      )}
                    </div>
                  </div>
                )}

                {callToActionButtons.length > 0 && (
                  <div className="create-template-action-group">
                    <p className="create-template-media-sample-label">
                      Call to Action
                      <em>Optional</em>
                    </p>
                    <div className="create-template-action-table">
                      {callToActionButtons.map(({ action, index }) =>
                        renderActionRow(action, index),
                      )}
                    </div>
                  </div>
                )}
              </div>
              </section>
            )}

            {!success && (
              <div className="create-template-form-footer">
                <button
                  type="submit"
                  className="create-template-submit"
                  disabled={submitting || !phoneReady}
                >
                  {submitting ? "Submitting…" : "Submit template"}
                </button>
              </div>
            )}
          </form>
        </div>

        <aside
          className={`create-template-preview${
            form.category === "authentication" ? " is-auth" : ""
          }`}
        >
          {form.category === "authentication" ? (
            <div className="create-template-auth-preview">
              <div className="create-template-auth-tabs">
                <button
                  type="button"
                  className={previewTab === "parameters" ? "is-active" : ""}
                  onClick={() => setPreviewTab("parameters")}
                >
                  Parameters
                </button>
                <button
                  type="button"
                  className={previewTab === "example" ? "is-active" : ""}
                  onClick={() => setPreviewTab("example")}
                >
                  Example
                </button>
              </div>
              <div className="create-template-auth-chat">
                <div className="create-template-auth-bubble">
                  <p className="create-template-auth-body">
                    {highlightTemplateVars(authPreviewBody)}
                    <span className="create-template-auth-time">6:54 PM</span>
                  </p>
                  <div className="create-template-auth-copy">
                    <FaCopy />
                    {authPreviewButton}
                  </div>
                </div>
              </div>
              <p className="create-template-auth-note">
                Text in brackets {"{{ }}"} are dynamic. They will update with
                your content when your message is sent.
              </p>
            </div>
          ) : (
            <>
              <div className="create-template-preview-head">
                <h2>Preview</h2>
                <span>
                  {form.category
                    ? form.category.charAt(0).toUpperCase() + form.category.slice(1)
                    : "Template"}
                  {form.type ? ` · ${form.type}` : ""}
                </span>
              </div>
              <div className="create-template-preview-body">
                <div
                  className={`create-template-wa-bubble${
                    form.type === "Location" ||
                    (form.type &&
                      form.type !== "Text" &&
                      form.type !== "Carousel")
                      ? " has-media"
                      : ""
                  }`}
                >
                  {form.type === "Location" && (
                    <div className="create-template-wa-location">
                      <div className="create-template-wa-map" aria-hidden>
                        <FaMapMarkerAlt />
                      </div>
                      <div className="create-template-wa-location-meta">
                        <strong>
                          {highlightTemplateVars("{{Location name}}")}
                        </strong>
                        <span>{highlightTemplateVars("{{Address}}")}</span>
                      </div>
                    </div>
                  )}
                  {form.type !== "Text" &&
                    form.type &&
                    form.type !== "Carousel" &&
                    form.type !== "Location" && (
                    <div
                      className={`create-template-wa-media${
                        form.type === "Video" ? " is-video" : ""
                      }`}
                    >
                      {form.type === "Image" && samplePreview ? (
                        <img src={samplePreview} alt="" />
                      ) : form.type === "Video" ? (
                        <span className="create-template-wa-play" aria-hidden>
                          <FaPlay />
                        </span>
                      ) : (
                        sampleFile?.name || `${form.type} header`
                      )}
                    </div>
                  )}
                  {previewHeader && form.type !== "Location" && (
                    <p className="create-template-wa-header">
                      {highlightTemplateVars(form.headertext)}
                    </p>
                  )}
                  <div className="create-template-wa-copy">
                    <p
                      className={`create-template-wa-body${
                        form.format.trim() ? "" : " create-template-wa-body-empty"
                      }`}
                    >
                      {form.format.trim()
                        ? highlightTemplateVars(form.format)
                        : "Message body will appear here"}
                      <span className="create-template-wa-time">{previewTime}</span>
                    </p>
                    {form.footer && (
                      <p className="create-template-wa-footer">{form.footer}</p>
                    )}
                  </div>
                  {form.actions.length > 0 && (
                    <div className="create-template-wa-buttons">
                      {(form.actions.length > 3
                        ? form.actions.slice(0, 2)
                        : form.actions
                      ).map((action, index) => {
                        const Icon = PREVIEW_BUTTON_ICONS[action.type] || FaReply;
                        return (
                          <span key={`${action.type}-${index}`}>
                            <Icon aria-hidden />
                            {action.title || "Quick Reply"}
                          </span>
                        );
                      })}
                      {form.actions.length > 3 && (
                        <button
                          type="button"
                          className={`create-template-wa-see-all${
                            previewSeeAll ? " is-open" : ""
                          }`}
                          onClick={() => setPreviewSeeAll((open) => !open)}
                        >
                          <FaList aria-hidden />
                          See all options
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {previewSeeAll && form.actions.length > 3 && (
                  <div className="create-template-wa-all-options">
                    {form.actions.map((action, index) => {
                      const Icon = PREVIEW_BUTTON_ICONS[action.type] || FaReply;
                      return (
                        <span key={`all-${action.type}-${index}`}>
                          <Icon aria-hidden />
                          {action.title || action.type}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="create-template-disclaimer">
                This is a graphical preview. The message customers receive may
                look slightly different on their device.
              </p>
            </>
          )}
        </aside>
      </div>

      {submitting && (
        <div className="create-template-overlay">
          <p>Submitting template — please do not navigate away.</p>
        </div>
      )}
    </div>
  );
};

export default CreateTemplate;
